import { ProductScope } from '@interfaces/auth';
/* O enum mora em `collaborators`, que é onde o escopo nasceu: o grant é quem o define. */
import { ReportScope } from '@interfaces/collaborators';

/**
 * Quais comunidades da seleção cada família de bloco do painel pode servir.
 *
 * O painel classificava a seleção INTEIRA por um papel único (`filters.userRole === 'AFFILIATED'`),
 * e numa seleção mista não existe valor único correto: a comunidade alcançada por grant não é de
 * afiliado, e tratar tudo como produtor entrega o faturamento cheio de uma comunidade onde a pessoa
 * só tem fatia. É o que a #360 registra.
 *
 * As regras abaixo são as MESMAS que a `report-api` declara nas variantes do `ProductScope`, e é de
 * propósito que sejam três e não uma: `ProducerProductScope` aceita só `OWNER`/`COPRODUCER`,
 * `NonReferrerProductScope` aceita também afiliado, e o `ProductScope` puro aceita os quatro. Cada
 * relatório escolheu a sua lá, porque a métrica não significa nada para os papéis de fora.
 *
 * **Produto sem entrada no descritor fica FORA de todas as famílias.** Não é o caso esperado — o
 * servidor devolve uma entrada por produto alcançado —, e a alternativa seria adivinhar a que
 * família ele pertence. Número faltando é ruim; número errado é pior, e é o que a #360 existe para
 * acabar.
 */
export type SubconjuntosDoPainel = {
  /** Blocos que falam pela comunidade: conversão, funil, recorrência, remarketing, métricas. */
  produtor: number[];
  /** Blocos da fatia de quem divulga. */
  afiliado: number[];
  /** Blocos que servem aos dois — hoje, valor do cliente. */
  ambos: number[];
};

const PAPEIS_DE_PRODUTOR = ['OWNER', 'COPRODUCER'];

export const subconjuntosDoPainel = (
  botConfigIds: number[],
  escopos: ProductScope[],
): SubconjuntosDoPainel => {
  const porProduto = new Map(
    escopos.map((escopo) => [Number(escopo.botConfigId), escopo]),
  );

  const admite = (
    id: number,
    aceita: (escopo: ProductScope) => boolean,
  ): boolean => {
    const escopo = porProduto.get(Number(id));

    return escopo ? aceita(escopo) : false;
  };

  /*
    `PRODUCT` entra nas duas famílias que admitem produto inteiro, e é o caso do colaborador com
    escopo de produto — quem a #360 existe para servir. Ele não tem papel, e é justamente por isso
    que a classificação por papel o excluía.
  */
  const eProdutor = (escopo: ProductScope) =>
    escopo.scope === ReportScope.PRODUCT ||
    (!!escopo.role && PAPEIS_DE_PRODUTOR.includes(escopo.role));

  const eAfiliado = (escopo: ProductScope) =>
    escopo.scope === ReportScope.COMMISSION && escopo.role === 'AFFILIATED';

  return {
    produtor: botConfigIds.filter((id) => admite(id, eProdutor)),
    afiliado: botConfigIds.filter((id) => admite(id, eAfiliado)),
    /* Sem indicador: a métrica de valor do cliente não significa nada para quem só indicou. */
    ambos: botConfigIds.filter((id) =>
      admite(id, (escopo) => eProdutor(escopo) || eAfiliado(escopo)),
    ),
  };
};
